/**
 * The dashboard: what the archive holds, how this year is going, and the way in.
 *
 * First item in the sidebar, no group of its own, and what the brand lockup in the
 * corner opens. It is a summary and a set of doors — every number and every point on the
 * chart leads to the page that lists what it counted, because a figure somebody cannot
 * open is a figure they have to take on trust.
 *
 * ALUMNI ARE COUNTED HERE, unlike every report in the app, and the three boxes say so on
 * their own faces: active on one line and alumni on the next. The reports answer "who are
 * we working with"; this answers "what is in the archive", and those are different
 * questions. The chart counts them too, and there it is not a preference but a
 * correctness matter — see the note above it.
 *
 * Not to be confused with **Updates Overview** (`pages/UpdatesOverview.jsx`), which is
 * the older landing page and still carries the archive-wide search box.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs } from '../lib/api'
import { useRelabel, useCode } from '../lib/terms'
import { YearOverYearChart, YearOverYearLegend } from '../components/charts'
import { SEARCH_INPUT_ID } from './UpdatesOverview'
import SearchResults from '../components/SearchResults'
import { MediaImage } from '../components/ImageUpload'
import FavoriteButton from '../components/Favorite'
import {
  Card, Badge, Empty, Loading, ErrorState, CountryLabel, SearchInput,
  fmtInt, headcount,
} from '../components/ui'
import { Icon } from '../components/Icon'
import { Picker } from '../components/Select'

/** Short labels for the axis, full ones for a link's accessible name. */
const MONTHS = [
  ['Jan', 'January'], ['Feb', 'February'], ['Mar', 'March'], ['Apr', 'April'],
  ['May', 'May'], ['Jun', 'June'], ['Jul', 'July'], ['Aug', 'August'],
  ['Sep', 'September'], ['Oct', 'October'], ['Nov', 'November'], ['Dec', 'December'],
]

/** Last day of a month, so a month's link is a closed range rather than an open one. */
const lastDay = (year, month) => new Date(Number(year), Number(month), 0).getDate()

/*
 * Every link out of this page carries `include_alumni=1`.
 *
 * The Updates page hides alumni units by default and every figure here counts them, so
 * without it a reader would click 5,164 and land on a list of 4,397 — the number
 * disagreeing with the page it opened, which is the one thing a dashboard must not do.
 */
const ALL = { include_alumni: 1 }

/**
 * How many favourites the panel asks for.
 *
 * All of them, in effect: a shortlist somebody keeps by hand runs to dozens, and the
 * panel scrolls rather than truncating. A ceiling all the same, because "all" of anything
 * is a promise this page cannot keep — the API's own limit is 500, and the whole point of
 * the panel is that it never grows the page it sits on.
 */
const FAVOURITES_LIMIT = 200

/**
 * A month, chosen exactly.
 *
 * The points on the chart are links and they are the quick way, but they cannot be the
 * only way: two years that track each other closely put their points a couple of pixels
 * apart, and no pointer can separate a 2.4px gap — click the older one and you open the
 * newer. Measured on the chart as it stands, five of thirty-two points are in that state.
 *
 * So this is the control the chart is a shortcut to, which is the same arrangement the
 * partner record uses for its own chart: a bar to click and a select beside it, on the
 * rule that an SVG click is a mouse-only affordance and needs a real control next to it.
 * Here the control is also the more precise of the two, which is the honest way round.
 *
 * Newest first, and it stops at the month we are in — there is nothing to show in a month
 * that has not happened.
 */
function MonthPicker({ years, through, hrefForPoint }) {
  const navigate = useNavigate()
  const months = useMemo(() => {
    const [lastYear, lastMonth] = String(through ?? '').split('-')
    const out = []
    for (const y of [...(years ?? [])].reverse()) {
      const top = y === lastYear ? Number(lastMonth) || 12 : 12
      for (let m = top; m >= 1; m--) {
        const mm = String(m).padStart(2, '0')
        out.push({ value: `${y}-${mm}`, label: `${MONTHS[m - 1][0]} ${y}`, year: y, month: mm })
      }
    }
    return out
  }, [years, through])

  return (
    <div className="month-pick">
      <label className="month-pick-label" htmlFor="month-pick" id="month-pick-label">
        Open a month
      </label>
      {/* Searchable, since there are a dozen a year back to the first: "mar 2019" finds
          one. It never holds a value — choosing a month leaves this page. */}
      <Picker
        inputId="month-pick"
        options={months}
        value={null}
        onChange={(v) => {
          const picked = months.find((m) => m.value === v)
          if (picked) navigate(hrefForPoint(picked.year, picked.month))
        }}
        placeholder="Choose…"
        aria-label="Open a month"
        isClearable={false}
        compact
        sx={{ width: 150 }}
      />
    </div>
  )
}

/**
 * One box of the three across the top: a heading, then label-and-number rows.
 *
 * A row with a `to` is a link, and most are. `value` is already formatted — the caller
 * decides whether an unanswered figure reads as 0 or as an em dash, which is not the
 * same question in every box.
 */
function StatBox({ title, rows }) {
  /* The headings and the row labels say "Partner Units" and "Partners", both of which an
     installation may have renamed — and neither reaches the screen through a component
     that relabels on its own, the way a Card title or a Field label does. */
  const t = useRelabel()
  return (
    /* Its own card, sharing `.card` with every other card in the app rather than three
       panels ruled out of one — and the three of them hold their row down to a phone the
       way the tiles on Updates Overview do, giving up padding and type size rather than
       the row. See .dash-boxes. */
    <section className="card statbox">
      <h2 className="statbox-title">{t(title)}</h2>
      <dl className="statbox-rows">
        {rows.map((r) => (
          <div className="statbox-row" key={r.label}>
            {/* Two labels, one shown at a time. Three boxes across a 360px screen leaves
                about a hundred pixels each, where "by Active Partner Units" is three
                lines of wrapped text — and it does not need to be there at all, because
                the box is already headed UPDATES. So the narrow one says only what the
                heading does not: Active, Alumni, This year. */}
            <dt>
              <span className="statbox-long">{t(r.label)}</span>
              <span className="statbox-short">{t(r.short ?? r.label)}</span>
            </dt>
            <dd>
              {r.to ? (
                <Link to={r.to} title={r.title}>
                  {r.value}
                </Link>
              ) : (
                <span title={r.title}>{r.value}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/** One of the doors below the chart: a name, and what is behind it. */
const DoorCard = ({ to, title, note }) => (
  <Link className="door" to={to}>
    <span className="door-text">
      <span className="door-title">{title}</span>
      <span className="door-note">{note}</span>
    </span>
  </Link>
)

/**
 * A favourite, compact: the face, who they are, and the figures — no description.
 *
 * The directory's own card carries a paragraph and is the better card for reading one
 * unit. This one exists to fit eight of them under a chart, so what it keeps is only what
 * identifies a unit at a glance: the face, the name, the code, where they are, and the two
 * figures. Anything else belongs on the record a click away.
 */
function FavouriteCard({ r, onPicked }) {
  const code = useCode()
  const people = headcount(r)
  const t = useRelabel()
  const navigate = useNavigate()

  /* The whole card opens the unit, the way a table row does and the way the directory's
     own card already does. The heart and the links inside go where they say instead, and
     a click that ends a text selection is somebody copying a code, not asking to
     navigate. The links are also what makes this reachable from the keyboard, a div with
     a handler on it being reachable from nothing. */
  const open = (e) => {
    if (e.target.closest('a,button,input,label,select')) return
    if (window.getSelection?.()?.toString()) return
    navigate(`/partners/${r.partner_id}`)
  }

  return (
    <div className="fav-card clickable" onClick={open}>
      {/* Out of the flow and over the corner, the way the directory's own card carries
          its heart — and for the same reason. In the row beside the name it was a 28px
          button on a 17px line, so it set that row's height and left eleven pixels of
          nothing beside it on every card. */}
      <div className="fav-fav">
        <FavoriteButton partnerId={r.partner_id} name={r.display_name} on onChange={onPicked} />
      </div>
      <Link className="fav-card-face" to={`/partners/${r.partner_id}`} tabIndex={-1} aria-hidden="true">
        {r.photo_media_id ? (
          <MediaImage id={r.photo_media_id} alt="" className="fav-photo" />
        ) : (
          <span className="fav-photo fav-photo-empty"><Icon name="person" size={24} /></span>
        )}
      </Link>
      <div className="fav-card-body">
        <Link className="fav-name truncate" to={`/partners/${r.partner_id}`}>
          {r.display_name}
        </Link>
        <Link className="mono tiny subtle" to={`/partners/${r.partner_id}`}>
          {code(r.s4ca_code)}
        </Link>
        <div className="tiny truncate">
          <CountryLabel id={r.country_id} name={r.country_name} flag={r.flag_emoji} />
        </div>
        <div className="fav-figures tiny">
          <span title={t('Partners in this unit')}><Icon name="person" size={12} /> {people.label}</span>
          <span title="Updates in the archive"><Icon name="mail" size={12} /> {fmtInt(r.newsletter_count)}</span>
          {r.status !== 'active' ? <Badge>{r.status}</Badge> : null}
        </div>
      </div>
    </div>
  )
}

/** By the reader's own clock, which is the one they are saying good morning by. */
const greeting = (hour = new Date().getHours()) =>
  hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'

export default function Dashboard({ user }) {
  const query = useApi('stats-dashboard', 'stats/dashboard')
  /* The same box as Updates Overview, the same id, and now the same behaviour: results
     appear as you type, in place of the dashboard, and clearing the box gives the
     dashboard back. `/` from any page focuses that id, and only one of these two pages
     is ever mounted, so there is never a second one to collide with. The term rides in
     `?q=` so Back from a result returns to the results rather than an empty box. */
  const [params, setParams] = useSearchParams()
  const [input, setInput] = useState(params.get('q') ?? '')
  const [q, setQ] = useState(params.get('q') ?? '')
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(input.trim())
      const next = new URLSearchParams()
      if (input.trim()) next.set('q', input.trim())
      setParams(next, { replace: true })
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])
  // Two characters, as on Updates Overview: below that the index answers with noise.
  const searching = q.length > 1
  /* The reader's own picks, straight off the directory endpoint — the same rows the
     Favorites page draws, asked for a page at a time rather than all of them. */
  const favourites = useApi(
    ['dashboard-favourites'],
    `partners${qs({ favorites: 1, include_alumni: 1, limit: FAVOURITES_LIMIT, sort: 'display_name' })}`
  )

  const d = query.data

  /* One month, one link — written once so the points on the chart and the select above
     them cannot drift apart about what "March 2025" means. */
  const monthHref = (year, month) =>
    `/newsletters${qs({
      ...ALL,
      from: `${year}-${month}-01`,
      to: `${year}-${month}-${lastDay(year, month)}`,
    })}`

  /* The chart's rows: twelve months, one column per year. Filled out to twelve here
     rather than in the Worker — a month with nothing in it is a zero on the line, and
     sending 36 rows to say what 20 of them say is a waste of the wire.
     
     Except after this month, where the current year gets null instead of nought. A zero
     is a month that happened and had nothing in it; November of a year that has not
     reached November is not a month with no updates, it is not a month yet. Drawn as a
     zero it was a flat line along the floor to December, which read as a catastrophic
     year rather than an unfinished one. Null ends the line at the last month there is
     something to say about, which is what the past years' lines do at December. */
  const chart = useMemo(() => {
    const byKey = new Map((d?.monthly ?? []).map((r) => [`${r.year}-${r.month}`, r.n]))
    // Where "now" is, off the Worker's clock rather than this browser's, so the month and
    // the years cannot disagree about which year is the current one.
    const [thisYear, thisMonth] = String(d?.through ?? '').split('-')
    const monthsSoFar = Number(thisMonth) || 12
    return MONTHS.map(([label, full], i) => {
      const month = String(i + 1).padStart(2, '0')
      const row = { label, full, month }
      for (const y of d?.years ?? []) {
        row[y] = y === thisYear && i + 1 > monthsSoFar ? null : byKey.get(`${y}-${month}`) ?? 0
      }
      return row
    })
  }, [d])

  if (query.isLoading) {
    return (
      <PageHeader title="Home">
        <Loading />
      </PageHeader>
    )
  }
  if (query.isError) {
    return (
      <PageHeader title="Home">
        <ErrorState error={query.error} retry={query.refetch} />
      </PageHeader>
    )
  }

  /* Nobody has been counted yet — every unit reads NULL until a registry file carrying
     the head-count columns has been imported over it (migration 0026). A confident 0
     would be an answer to a question nobody has asked, so the box says so instead. */
  const counted = d.people.counted > 0
  const people = (n) => (counted ? fmtInt(n) : '—')
  const uncounted = counted ? undefined : 'No partner unit has a head count yet — import a registry file that carries the Staff and non-Staff Partners columns'

  return (
    <PageHeader
      title="Home"
      actions={
        user.caps.create ? (
          <Link className="btn btn-sm btn-primary" to="/newsletters/new">
            + New Update
          </Link>
        ) : null
      }
    >
      <div className="col dash">
        {/* Out of the top bar and onto the page, where it has room to be a greeting
            rather than a subtitle — set large, the way Bloomerang opens its home page. */}
        <div className="greeting">
          <h2 className="greeting-title">
            {greeting()}
            {user?.name ? `, ${user.name}` : ''}!
          </h2>
          <p className="greeting-sub">What’s happening with {user?.orgName || 'the archive'} today</p>
        </div>
        {/* Above the figures, as on Updates Overview and for the same reason: searching
            the archive is the commonest reason to open it. */}
        <SearchInput
          id={SEARCH_INPUT_ID}
          value={input}
          onChange={setInput}
          placeholder="Search every update and prayer request…"
          title="Press / from any page to search"
          aria-label="Search everything"
        />

        {searching ? (
          <SearchResults q={q} />
        ) : (
        <>
        <div className="dash-boxes">
          <StatBox
            title="Updates"
            rows={[
              {
                label: 'by Active Partner Units',
                short: 'Active',
                value: fmtInt(d.updates.by_active),
                to: '/newsletters',
                title: 'Every update from a partner unit that is not alumni',
              },
              {
                /* Not a link, and the one figure here that is not. The Updates list can
                   put alumni back in but cannot show *only* them — there is no
                   partner-status filter on an update — and a link that landed on all
                   5,164 while claiming to be the 767 would be worse than no link. */
                label: 'by Alumni Partner Units',
                short: 'Alumni',
                value: fmtInt(d.updates.by_alumni),
                title: 'Updates from units marked alumni. The Updates page includes these when its Alumni switch is on',
              },
              {
                label: 'Updates this Year',
                short: 'This year',
                value: fmtInt(d.updates.this_year),
                to: `/newsletters${qs({ ...ALL, year: d.years[d.years.length - 1] })}`,
                title: `Published in ${d.years[d.years.length - 1]}, alumni units included`,
              },
            ]}
          />
          <StatBox
            title="Partner Units"
            rows={[
              {
                label: 'Active Partner Units',
                short: 'Active',
                value: fmtInt(d.units.active),
                to: '/partners',
                title:
                  'Active partner units the registry counts staff in — exactly what the ' +
                  'register opens on. The two rows below it are wider than that page ' +
                  'opens, so they say so in their links; the three do not add up, and the ' +
                  'gap is the units marked inactive and the ones counted as non-staff ' +
                  'partners only',
              },
              {
                label: 'Alumni Partner Units',
                short: 'Alumni',
                value: fmtInt(d.units.alumni),
                to: `/partners${qs({ status: 'alumni' })}`,
                title: 'Past partner units',
              },
              {
                label: 'Total Partner Units',
                short: 'Total',
                value: fmtInt(d.units.total),
                to: `/partners${qs({ include_alumni: 1 })}`,
                title: 'Every partner unit in the archive',
              },
            ]}
          />
          <StatBox
            title="Partners"
            rows={[
              {
                label: 'Active Partners',
                short: 'Active',
                value: people(d.people.active),
                to: counted ? '/partners' : undefined,
                title: uncounted ?? 'People in the partner units that are not alumni — staff and non-staff together',
              },
              {
                label: 'Alumni Partners',
                short: 'Alumni',
                value: people(d.people.alumni),
                to: counted ? `/partners${qs({ status: 'alumni' })}` : undefined,
                title: uncounted ?? 'People in the units marked alumni',
              },
              {
                label: 'Total Partners',
                short: 'Total',
                value: people(d.people.total),
                to: counted ? `/partners${qs({ include_alumni: 1 })}` : undefined,
                title: uncounted ?? 'Everybody the registry counts, in every unit',
              },
            ]}
          />
        </div>

        {/* Alumni are in this chart on purpose, and it is not the same choice the boxes
            above make. There it is a preference; here it is arithmetic. A unit that
            became alumni this year has fifteen years of updates behind it, and leaving
            them out would take those years off the chart as well — so last year's line
            would move every time somebody retires, and a trend that rewrites its own
            past is worse than no trend at all. */}
        {/* The legend rides in the header beside the title, and the body starts flush
            under it — between them that was most of the card's empty space, and the years
            read better next to the title than in a band of their own. */}
        <Card
          title="Partner Updates Year over Year"
          sub="Every update, alumni units included. Pick a month or a year to open it"
          bodyClass="dash-chart-body"
          actions={
            <>
              <YearOverYearLegend
                years={d.years}
                hrefForYear={(year) => `/newsletters${qs({ ...ALL, year })}`}
              />
              <MonthPicker years={d.years} through={d.through} hrefForPoint={monthHref} />
            </>
          }
        >
          <YearOverYearChart data={chart} years={d.years} hrefForPoint={monthHref} />
        </Card>

        <div className="dash-doors">
          <DoorCard
            to="/newsletter-issues"
            title="Newsletters"
            note={`${fmtInt(d.shelves.issues)} issue${d.shelves.issues === 1 ? '' : 's'} on the shelf`}
          />
          <DoorCard
            to="/annual-reports"
            title="Annual Reports"
            note={`${fmtInt(d.shelves.reports)} report${d.shelves.reports === 1 ? '' : 's'} on the shelf`}
          />
          {/* Prayer requests carried by an update the archive has starred, in the last
              thirty days. Both halves matter: highlighted is the archive saying this
              one matters, and thirty days is what makes it something to pray about now
              rather than a reading of the corpus.
              
              Its own route rather than this page's query string, so the sidebar can open
              the same reading — a nav entry cannot carry a window that moves. */}
          <DoorCard
            to="/prayer-needs"
            title="Current Prayer Needs"
            note={
              d.shelves.prayer_needs_30
                ? `${fmtInt(d.shelves.prayer_needs_30)} in the last 30 days`
                : 'None in the last 30 days'
            }
          />
          {/* The Register's three pages, in the sidebar's order. Doors rather than a
              fourth StatBox: the boxes above are readings of the archive — how many, how
              many alumni, how many people — and these three are simply the way in.

              Each note counts what its page opens on, alumni left out, which is what the
              page itself shows. A door whose number disagreed with the page behind it
              would be worse than a door with no number on it at all. */}
          <DoorCard
            to="/partners"
            title="Partner Units"
            note={`${fmtInt(d.kinds.staff)} in the register`}
          />
          <DoorCard
            to="/training-schools"
            title="Training Schools"
            note={`${fmtInt(d.kinds.training)} in the register`}
          />
          <DoorCard
            to="/projects"
            title="Projects"
            note={`${fmtInt(d.kinds.project)} in the register`}
          />
        </div>

        <Card
          title="Favorites"
          sub={
            favourites.data?.total
              ? `${fmtInt(favourites.data.total)} partner unit${favourites.data.total === 1 ? '' : 's'} you follow`
              : undefined
          }
          /* The panel holds every favourite, so this is not "the rest of them" — it is
             the page where they can be filtered, sorted and taken away as a CSV, which a
             scrolling strip is not trying to be. */
          actions={
            favourites.data?.total ? (
              <Link className="btn btn-sm" to="/favorites">
                Open Favorites
              </Link>
            ) : null
          }
        >
          {favourites.isLoading ? (
            <Loading rows={2} />
          ) : favourites.isError ? (
            <ErrorState error={favourites.error} retry={favourites.refetch} />
          ) : favourites.data?.rows?.length ? (
            /* Scrolls rather than running the page long. The photographs inside are
               fetched as they come near the viewport (see GatedImage) and a card scrolled
               out of this box is clipped by it, which an IntersectionObserver counts as
               out of sight — so a reader with sixty favourites does not pay for sixty
               pictures on arrival. */
            <div className="fav-scroll">
              <div className="fav-strip">
                {favourites.data.rows.map((r) => (
                  <FavouriteCard key={r.partner_id} r={r} onPicked={() => favourites.refetch()} />
                ))}
              </div>
            </div>
          ) : (
            <Empty icon="heart" title="You have not chosen any partner units yet">
              Open Partner Units and press the <Icon name="heart" size={12} /> beside anyone you want to follow. They will
              be gathered here.
            </Empty>
          )}
        </Card>
        </>
        )}
      </div>
    </PageHeader>
  )
}

